import { useMutation, useQueryClient } from "@tanstack/react-query";

import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

import { approveMerge } from "./api";
import type { ApproveMergeRequest } from "./api";
import { isTaskStateRefusal } from "./logic";
import { refusalText } from "./refusalText";
import { ApiError } from "../../api/apiError";
import type { TaskResponse } from "../queue/api";
import { invalidateTaskViews } from "../queue/invalidateTaskViews";

// Отказы, после которых человеку нужен свежий снимок гейта: состояние
// на хостинге изменилось или не совпало с увиденным.
const GATE_REFRESH_CODES: ReadonlySet<string> = new Set([
  "head_moved",
  "base_moved",
  "gate_blocked",
  "gate_unavailable",
  "approval_stale",
  "pr_closed",
  "invalid_acknowledgement",
]);

// Подтверждение слияния (AWAITING_APPROVAL → DONE). На успех
// инвалидируем кэши задачи, журнала, списка и очереди; на конфликт статуса
// задачи (`409 not_awaiting_approval` / `task_cancelled`) — те же кэши и
// уведомление с текстом отказа; на отказ из GATE_REFRESH_CODES — снимок
// гейта. Инвалидация по смысловому префиксу
// матчит запись любого org-скоупа (см. `src/query/scope.ts`).
export function useApproveMerge(
  taskId: string,
): ReturnType<typeof useMutation<TaskResponse, Error, ApproveMergeRequest>> {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation<TaskResponse, Error, ApproveMergeRequest>({
    mutationFn: (body) => approveMerge(taskId, body),
    onSuccess: () => {
      invalidateTaskViews(queryClient, taskId);
    },
    // Конфликт статуса задачи: карточку перечитываем, а текст показываем
    // уведомлением здесь — после перечитывания баннер размонтируется и Alert
    // пропал бы вместе с ним.
    onError: (error) => {
      if (isTaskStateRefusal(error)) {
        invalidateTaskViews(queryClient, taskId);
        notifications.show({
          message: refusalText(error, t),
          color: "yellow",
        });
        return;
      }
      if (
        error instanceof ApiError &&
        error.code !== null &&
        GATE_REFRESH_CODES.has(error.code)
      ) {
        void queryClient.invalidateQueries({
          queryKey: ["merge-gate", taskId],
        });
      }
    },
  });
}
